import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import TeamPreviewField from "../components/TeamPreviewField";
import { UserContext } from "../context/UserContext";
import { CRICKET_API_URL } from "../config";
import {
  getPlayerId,
  normalizeTeamSelection,
  resolvePlayerSelectionId,
} from "../utils/playerIdentity";
import {
  getPlayerLineupIndicator,
  getResolvedLineupStatus,
  isLineupOutForMatch,
} from "../utils/playerLineupStatus";

const DEFAULT_TEAM_IMAGE = "https://h.cricapi.com/img/icon512.png";

const normalizeTeamCode = (value = "") =>
  String(value || "").trim().toUpperCase();

const buildPlayerAvailabilityKey = (playerName = "", teamCode = "") =>
  `${String(playerName || "").trim().toLowerCase()}::${normalizeTeamCode(teamCode)}`;

const buildNormalizedTeam = (team = {}, fallbackTeamId = null) => {
  if (!team || typeof team !== "object") return null;

  const normalizedTeam = normalizeTeamSelection(team);
  const players = normalizedTeam.players || [];

  return {
    ...normalizedTeam,
    _id: team._id || fallbackTeamId || team.id || null,
    id: team.id || team._id || fallbackTeamId || null,
    players,
    captainId: resolvePlayerSelectionId({
      players,
      playerId: normalizedTeam.captainId,
      player: normalizedTeam.captain,
      playerIndex: normalizedTeam.captainIndex,
    }),
    viceCaptainId: resolvePlayerSelectionId({
      players,
      playerId: normalizedTeam.viceCaptainId || normalizedTeam.vcId,
      player: normalizedTeam.viceCaptain,
      playerIndex: normalizedTeam.viceCaptainIndex,
    }),
  };
};

const buildTeamFromState = (state = {}, routeTeamId = null) => {
  const stateTeam = state?.selectedTeam || state?.team;
  if (!stateTeam) return null;

  return buildNormalizedTeam(
    {
      ...stateTeam,
      players: state?.players || stateTeam.players || [],
      captainId: state?.captainId || stateTeam.captainId,
      viceCaptainId:
        state?.viceCaptainId ||
        state?.vcId ||
        stateTeam.viceCaptainId ||
        stateTeam.vcId,
    },
    routeTeamId,
  );
};

const normalizeRole = (role = "") => {
  const normalizedRole = String(role || "").toUpperCase();

  if (normalizedRole.includes("WK") || normalizedRole.includes("KEEPER")) return "WK";
  if (normalizedRole.includes("BAT")) return "BAT";
  if (normalizedRole.includes("AR") || normalizedRole.includes("ALL")) return "AR";
  if (normalizedRole.includes("BOWL")) return "BOWL";
  return normalizedRole || "BAT";
};

const getResolvedPlayerLineupMeta = (player = {}, lineupLookup = {}) => {
  if (getResolvedLineupStatus(player) || player?.playedLastMatch) {
    return player;
  }

  const playerId = getPlayerId(player);
  if (playerId && lineupLookup?.byId?.[playerId]) {
    return lineupLookup.byId[playerId];
  }

  const playerAvailabilityKey = buildPlayerAvailabilityKey(player?.name, player?.team);
  if (playerAvailabilityKey && lineupLookup?.byKey?.[playerAvailabilityKey]) {
    return lineupLookup.byKey[playerAvailabilityKey];
  }

  return player;
};

function TeamPreview() {
  const navigate = useNavigate();
  const location = useLocation();
  const { teamId: routeTeamId } = useParams();
  const { user } = useContext(UserContext);

  const navigationState = useMemo(() => location.state || {}, [location.state]);
  const stateMatchData = useMemo(
    () => navigationState.matchData || {},
    [navigationState.matchData],
  );
  const actualMatchId =
    stateMatchData?.matchId ||
    stateMatchData?.id ||
    stateMatchData?._id ||
    null;
  const stateTeamNames = useMemo(
    () => navigationState.teamNames || {},
    [navigationState.teamNames],
  );
  const stateTeam = useMemo(
    () => buildTeamFromState(navigationState, routeTeamId),
    [navigationState, routeTeamId],
  );
  const token = localStorage.getItem("token");
  const headers = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token],
  );

  const [fetchedTeam, setFetchedTeam] = useState(null);
  const [playerAvailabilityLookup, setPlayerAvailabilityLookup] = useState({
    byId: {},
    byKey: {},
  });
  const [loading, setLoading] = useState(
    Boolean(routeTeamId && (!stateTeam || (stateTeam.players || []).length === 0)),
  );

  useEffect(() => {
    let isActive = true;

    const hasUsableStateTeam =
      stateTeam &&
      (!routeTeamId ||
        String(stateTeam._id || stateTeam.id || "") === String(routeTeamId)) &&
      Array.isArray(stateTeam.players) &&
      stateTeam.players.length > 0;

    if (!routeTeamId || hasUsableStateTeam) {
      setLoading(false);
      return () => {
        isActive = false;
      };
    }

    const fetchTeam = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${CRICKET_API_URL}/api/cricket/team-details/${routeTeamId}`,
          { headers },
        );

        if (!isActive) return;

        setFetchedTeam(buildNormalizedTeam(response.data, routeTeamId));
      } catch (error) {
        if (isActive) {
          setFetchedTeam(null);
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    fetchTeam();

    return () => {
      isActive = false;
    };
  }, [headers, routeTeamId, stateTeam]);

  const selectedTeam = useMemo(
    () => buildNormalizedTeam(fetchedTeam || stateTeam, routeTeamId),
    [fetchedTeam, routeTeamId, stateTeam],
  );
  const playersList = useMemo(() => selectedTeam?.players || [], [selectedTeam]);

  const captainId = resolvePlayerSelectionId({
    players: playersList,
    playerId: navigationState.captainId || selectedTeam?.captainId,
    player: navigationState.captain || selectedTeam?.captain,
    playerIndex: navigationState.captainIndex ?? selectedTeam?.captainIndex,
  });
  const viceCaptainId = resolvePlayerSelectionId({
    players: playersList,
    playerId:
      navigationState.viceCaptainId ||
      navigationState.vcId ||
      selectedTeam?.viceCaptainId,
    player: navigationState.viceCaptain || selectedTeam?.viceCaptain,
    playerIndex:
      navigationState.viceCaptainIndex ?? selectedTeam?.viceCaptainIndex,
  });

  const playerTeamCodes = useMemo(
    () => [
      ...new Set(
        playersList
          .map((player) => normalizeTeamCode(player.team))
          .filter(Boolean),
      ),
    ],
    [playersList],
  );

  const t1Code = normalizeTeamCode(
    stateMatchData.homeTeam?.code ||
      stateMatchData.team1?.code ||
      stateTeamNames.team1 ||
      playerTeamCodes[0] ||
      "T1",
  );
  const t2Code = normalizeTeamCode(
    stateMatchData.awayTeam?.code ||
      stateMatchData.team2?.code ||
      stateTeamNames.team2 ||
      playerTeamCodes.find((teamCode) => teamCode !== t1Code) ||
      playerTeamCodes[1] ||
      "T2",
  );

  const groupedPlayers = useMemo(
    () => ({
      wk: playersList.filter((player) => normalizeRole(player.role) === "WK"),
      bat: playersList.filter((player) => normalizeRole(player.role) === "BAT"),
      ar: playersList.filter((player) => normalizeRole(player.role) === "AR"),
      bowl: playersList.filter((player) => normalizeRole(player.role) === "BOWL"),
    }),
    [playersList],
  );

  const t1Count = playersList.filter(
    (player) => normalizeTeamCode(player.team) === t1Code,
  ).length;
  const t2ResolvedCount = playersList.filter(
    (player) => normalizeTeamCode(player.team) === t2Code,
  ).length;
  const t2Count = t2ResolvedCount || Math.max(playersList.length - t1Count, 0);

  const totalCreditsUsed = playersList.reduce(
    (sum, player) => sum + Number(player.credits || player.price || 0),
    0,
  );
  const creditsLeft = Math.max(0, 100 - totalCreditsUsed).toFixed(1);
  const previewTitle =
    selectedTeam?.teamName ||
    (selectedTeam?.teamNumber ? `Team ${selectedTeam.teamNumber}` : null) ||
    user?.name ||
    user?.username ||
    "My Team";
  const shouldShowPlayingStatusDots = isLineupOutForMatch(stateMatchData);

  useEffect(() => {
    let isActive = true;

    if (!shouldShowPlayingStatusDots || !actualMatchId || playersList.length === 0) {
      setPlayerAvailabilityLookup({ byId: {}, byKey: {} });
      return () => {
        isActive = false;
      };
    }

    const fetchPlayerAvailability = async () => {
      try {
        const response = await axios.get(
          `${CRICKET_API_URL}/api/cricket/match/${actualMatchId}/live`,
        );
        const liveMatchData = response.data || {};
        const homeTeamCode = normalizeTeamCode(
          stateMatchData?.team1?.code ||
            stateMatchData?.homeTeam?.code ||
            stateTeamNames?.team1 ||
            liveMatchData?.homeTeam?.code ||
            liveMatchData?.event_home_team?.substring(0, 3) ||
            t1Code,
        );
        const awayTeamCode = normalizeTeamCode(
          stateMatchData?.team2?.code ||
            stateMatchData?.awayTeam?.code ||
            stateTeamNames?.team2 ||
            liveMatchData?.awayTeam?.code ||
            liveMatchData?.event_away_team?.substring(0, 3) ||
            t2Code,
        );
        const fantasyPlayers = [
          ...((liveMatchData?.homeTeam?.players || liveMatchData?.squads?.homeTeam || []).map((player) => ({
            ...player,
            team: normalizeTeamCode(player?.team || homeTeamCode),
          }))),
          ...((liveMatchData?.awayTeam?.players || liveMatchData?.squads?.awayTeam || []).map((player) => ({
            ...player,
            team: normalizeTeamCode(player?.team || awayTeamCode),
          }))),
        ];
        const nextAvailabilityLookup = { byId: {}, byKey: {} };

        fantasyPlayers.forEach((player) => {
          const lineupStatus = getResolvedLineupStatus(player);
          if (!lineupStatus && !player?.playedLastMatch) return;

          const normalizedPlayer = {
            ...player,
            team: normalizeTeamCode(player?.team),
            playedLastMatch: Boolean(player?.playedLastMatch),
          };

          const fantasyPlayerId = getPlayerId(player);
          if (fantasyPlayerId) {
            nextAvailabilityLookup.byId[fantasyPlayerId] = normalizedPlayer;
          }

          const playerAvailabilityKey = buildPlayerAvailabilityKey(player?.name, player?.team);
          if (playerAvailabilityKey) {
            nextAvailabilityLookup.byKey[playerAvailabilityKey] = normalizedPlayer;
          }
        });

        if (isActive) {
          setPlayerAvailabilityLookup(nextAvailabilityLookup);
        }
      } catch (error) {
        if (isActive) {
          setPlayerAvailabilityLookup({ byId: {}, byKey: {} });
        }
      }
    };

    fetchPlayerAvailability();

    return () => {
      isActive = false;
    };
  }, [
    actualMatchId,
    playersList.length,
    shouldShowPlayingStatusDots,
    stateMatchData,
    stateTeamNames,
    t1Code,
    t2Code,
  ]);

  if (loading) {
    return (
      <div className="vh-100 d-flex flex-column align-items-center justify-content-center bg-dark text-white">
        <div className="spinner-border text-light" role="status"></div>
      </div>
    );
  }

  if (!selectedTeam || playersList.length === 0) {
    return (
      <div className="vh-100 d-flex flex-column align-items-center justify-content-center bg-dark text-white">
        <h5>No Team Data Found!</h5>
        <button className="btn btn-primary mt-3 rounded-pill px-4" onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#11141A", display: "flex", flexDirection: "column" }}>
      <div className="sticky-top shadow-sm" style={{ zIndex: 1040 }}>
        <div className="d-flex justify-content-between align-items-center px-3 py-3" style={{ background: "linear-gradient(135deg, #800000 0%, #4A0000 100%)" }}>
          <div className="d-flex align-items-center gap-3">
            <i className="bi bi-x-lg text-white fs-5" onClick={() => navigate(-1)} style={{ cursor: "pointer" }}></i>
            <span className="text-white fw-bold fs-6">{previewTitle}</span>
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center px-3 py-2 text-white shadow-sm" style={{ backgroundColor: "#2c3038" }}>
          <div className="d-flex flex-column">
            <span className="text-white-50 fw-bold" style={{ fontSize: "10px" }}>Players</span>
            <span className="fw-bold" style={{ fontSize: "13px" }}>
              {playersList.length}
              <span className="text-white-50 fw-normal">/11</span>
            </span>
          </div>

          <div className="d-flex align-items-center gap-2">
            <img
              src={stateMatchData.homeTeam?.logo || stateMatchData.homeTeam?.image || stateMatchData.team1?.image || DEFAULT_TEAM_IMAGE}
              alt=""
              className="rounded-circle bg-white"
              style={{ width: "22px", height: "22px", padding: "1px" }}
            />
            <div className="d-flex align-items-center bg-dark rounded px-2 py-1 border border-secondary">
              <span className="fw-bold text-dark bg-white px-1 rounded-sm" style={{ fontSize: "10px" }}>{t1Code}</span>
              <span className="fw-bold mx-2" style={{ fontSize: "13px" }}>{t1Count} : {t2Count}</span>
              <span className="fw-bold text-white-50 bg-secondary px-1 rounded-sm" style={{ fontSize: "10px" }}>{t2Code}</span>
            </div>
            <img
              src={stateMatchData.awayTeam?.logo || stateMatchData.awayTeam?.image || stateMatchData.team2?.image || DEFAULT_TEAM_IMAGE}
              alt=""
              className="rounded-circle bg-white"
              style={{ width: "22px", height: "22px", padding: "1px" }}
            />
          </div>

          <div className="d-flex flex-column text-end">
            <span className="text-white-50 fw-bold" style={{ fontSize: "10px" }}>Credits Left</span>
            <span className="fw-bold" style={{ fontSize: "13px" }}>{creditsLeft}</span>
          </div>
        </div>
      </div>

      <TeamPreviewField
        className="flex-grow-1"
        groupedPlayers={groupedPlayers}
        captainId={captainId}
        viceCaptainId={viceCaptainId}
        teamOneCode={t1Code}
        getPlayerMetaText={(player) =>
          `${player.credits || player.price || player.event_player_credit || "9.0"} Cr`
        }
        getStatusIndicator={(player) => {
          if (!shouldShowPlayingStatusDots) return null;
          return getPlayerLineupIndicator(
            getResolvedPlayerLineupMeta(player, playerAvailabilityLookup),
          );
        }}
      />
    </div>
  );
}

export default TeamPreview;
